// Shared Add/Edit team-member dialog for Admin → Organization (follow-on to #558).
//
// One component, two modes. Field order (agreed stack):
//   1. Name + Work Role              — both modes
//   2. Email                         — add only; on edit the member email shows as
//                                      the dialog description (PUT contract at
//                                      admin_users.py:304 accepts no email)
//   3. Password                      — add only (the create contract requires it)
//   4. Organization Role             — edit only ┐ PillarRoleEditor, which renders
//   5. Special Permissions           — edit only ├ the three sections in this order
//   6. Department Access             — edit only ┘
//   7. Groups                        — both modes (kept last; shared field)
// Profile Photo — edit only: add keeps the creation form minimal (the POST
// contract still accepts an optional `picture`; creates send picture: '').
//
// UX contract — fixed dialog header and action footer, scrollbar only inside
// the field area: the panel is a flex column capped at 85vh; header and footer
// are shrink-0 with borders while only the field container scrolls. Previously
// the whole edit panel scrolled, drifting the title and the save button out of
// view. The page keeps all API wiring (POST create vs PUT edit) — this
// component is presentational and receives form/setForm/handlers as props.
import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../ui/dialog';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import PillarRoleEditor from './PillarRoleEditor';
import { computeEffectiveAccess } from '../../utils/orgRoles';
import { WORK_ROLE_LABELS as ROLE_LABELS, ORG_ROLE_LABELS, pillarLabelOf as pillarLabel } from '../../utils/roleLabels';

export default function UserFormDialog({
    open,
    onOpenChange,
    mode,
    form,
    setForm,
    groups = [],
    saving = false,
    onSubmit,
    editUser,
    onPillarChange,
}) {
    const isEdit = mode === 'edit';

    // ─── Effective-access preview + no-false-positive guard (#558 follow-on) ──
    // The backend re-derives the legacy work role from the org role and the
    // highest department role on every save (rbac.sync_legacy_fields), so a
    // save can legitimately store something else than the picker shows
    // ("saved but didn't stick"). The dialog predicts that final state live,
    // blocks mismatched picks on save, and warns when a single department
    // Admin makes a Member a full organization admin.
    // ROLE_LABELS / ORG_ROLE_LABELS / pillarLabel are shared exports from
    // utils/roleLabels.js so the dialog preview and the page verdict toasts
    // stay in lockstep (dedupe, phase-1 audit round 2).
    const effective = isEdit ? computeEffectiveAccess(form.org_role, form.pillar_roles) : null;
    const chosenRole = form.role || 'sales_rep';
    // Locked: an org admin/owner always stores the Admin work role (read-only picker).
    const workRoleLocked = !!effective && effective.origin === 'org_role';
    // Conflict: the picked work role is NOT what the backend will end up storing.
    const roleConflict = !!effective && effective.effectiveWorkRole !== chosenRole;
    // Elevation: a single department Admin/Developer grants full org-admin power.
    const pillarElevated = !!effective && effective.origin === 'pillar' && effective.isOrgAdmin;
    const conflictReason = (() => {
        if (!effective) return '';
        const chosen = ROLE_LABELS[chosenRole] || chosenRole;
        if (effective.origin === 'org_role') {
            return `"${chosen}" would be overwritten on save — Organization Role "${ORG_ROLE_LABELS[form.org_role] || form.org_role}" grants full access, so the work role is always stored as Admin.`;
        }
        const dept = effective.driverPillarId ? pillarLabel(effective.driverPillarId) : null;
        if (effective.effectiveWorkRole === 'manager') {
            return `"${chosen}" would be overwritten on save — department access drives this member and the highest department role (${dept}) is Manager, so the work role is stored as Manager.`;
        }
        if (effective.isOrgAdmin) {
            return `"${chosen}" would be overwritten on save — the ${dept} Admin department role drives the work role (stored as Admin) and also grants full organization-admin power.`;
        }
        if (effective.origin === 'pillar') {
            return `"${chosen}" would be overwritten on save — department access drives this member and the highest department role (${dept}) is User-level access, so the work role is stored as Sales Rep.`;
        }
        return `"${chosen}" would be overwritten on save — members without a department role of their own are stored as Sales Rep. Grant a department role (or promote to Org Admin) for a higher work role.`;
    })();
    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-lg flex flex-col max-h-[85vh] p-0 gap-0 overflow-hidden">
                <DialogHeader className="shrink-0 border-b border-stone-100 px-5 py-4">
                    <DialogTitle>{isEdit ? 'Edit Team Member' : 'Add Team Member'}</DialogTitle>
                    <DialogDescription>
                        {isEdit ? (editUser?.email || '') : 'Create a new user in your organization'}
                    </DialogDescription>
                </DialogHeader>
                <form onSubmit={isEdit ? undefined : onSubmit} className="flex flex-col flex-1 min-h-0">
                    <div
                        className="flex-1 min-h-0 overflow-y-auto px-5 py-4 space-y-4"
                        data-testid={isEdit ? 'edit-user-form' : 'add-user-form'}
                    >
                        {/* 1. Profile Photo — edit only: add keeps the creation form
                              minimal; the POST contract still accepts an optional
                              `picture`, so creates send picture: '' */}
                        {isEdit && (
                            <div>
                                <Label>Profile Photo</Label>
                                <div className="flex items-center gap-3 mt-1">
                                    {form.picture ? (
                                        <img
                                            src={form.picture}
                                            alt=""
                                            className="w-10 h-10 rounded-full object-cover"
                                            data-testid="user-form-photo-preview"
                                        />
                                    ) : (
                                        <div
                                            className="w-10 h-10 rounded-full bg-stone-100 flex items-center justify-center text-stone-400 text-sm font-medium"
                                            data-testid="user-form-photo-placeholder"
                                        >
                                            {form.name?.charAt(0) || '?'}
                                        </div>
                                    )}
                                    <Input
                                        className="flex-1"
                                        value={form.picture || ''}
                                        onChange={(e) => setForm((f) => ({ ...f, picture: e.target.value }))}
                                        placeholder="Profile photo URL"
                                        data-testid="user-form-photo"
                                    />
                                </div>
                            </div>
                        )}
                        {/* 2. Name + Work Role — both modes */}
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <Label>Name</Label>
                                <Input
                                    value={form.name || ''}
                                    onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                                    placeholder="John Smith"
                                    className="mt-1"
                                    data-testid={isEdit ? 'edit-user-name' : 'add-user-name'}
                                />
                            </div>
                            <div>
                                <Label>Work Role</Label>
                                <select
                                    value={workRoleLocked ? 'admin' : chosenRole}
                                    onChange={(e) => setForm((f) => ({ ...f, role: e.target.value }))}
                                    disabled={workRoleLocked}
                                    title={workRoleLocked ? 'Org Admins always have the Admin work role' : undefined}
                                    className={`w-full mt-1 px-3 py-2 text-sm border border-stone-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-stone-900/10${workRoleLocked ? ' bg-stone-100 text-stone-500 cursor-not-allowed' : ''}`}
                                    data-testid={isEdit ? 'edit-user-workrole' : 'add-user-workrole'}
                                >
                                    <option value="sales_rep">Sales Rep</option>
                                    <option value="manager">Manager</option>
                                    <option value="admin">Admin</option>
                                </select>
                            </div>
                        </div>
                        <p className="text-[11px] text-stone-400 -mt-2">
                            {workRoleLocked
                                ? 'Org Admins always have the Admin work role — full product access everywhere.'
                                : 'Work role = product access level. Admin here also grants org-admin visibility.'}
                        </p>
                        {isEdit && effective && (
                            <div
                                data-testid="effective-access-preview"
                                className={`rounded-lg border text-xs px-3 py-2 space-y-1 ${
                                    roleConflict
                                        ? 'border-amber-300 bg-amber-50 text-amber-800'
                                        : pillarElevated
                                            ? 'border-amber-200 bg-amber-50/60 text-amber-800'
                                            : 'border-stone-200 bg-stone-50 text-stone-600'
                                }`}
                            >
                                <div className="flex items-center justify-between gap-2">
                                    <span>
                                        Will save as: <span className="font-semibold">{ORG_ROLE_LABELS[form.org_role] || form.org_role || 'Member'}</span> · Work role{' '}
                                        <span className="font-semibold">{ROLE_LABELS[effective.effectiveWorkRole] || effective.effectiveWorkRole}</span>
                                        {pillarElevated && effective.driverPillarId ? ` — treated as Org Admin via ${pillarLabel(effective.driverPillarId)}` : ''}
                                    </span>
                                    {roleConflict && (
                                        <button
                                            type="button"
                                            data-testid="apply-effective-workrole"
                                            onClick={() => setForm((f) => ({ ...f, role: effective.effectiveWorkRole }))}
                                            className="shrink-0 px-2 py-1 rounded-md bg-amber-800 text-white hover:bg-amber-900"
                                        >
                                            Use {ROLE_LABELS[effective.effectiveWorkRole] || effective.effectiveWorkRole}
                                        </button>
                                    )}
                                </div>
                                {roleConflict && (
                                    <p data-testid="effective-access-conflict">{conflictReason}</p>
                                )}
                                {pillarElevated && !roleConflict && (
                                    <p data-testid="effective-access-elevated">
                                        A department Admin is a full organization admin today. If that is the intent, set Organization Role to Org Admin so the record matches the power.
                                    </p>
                                )}
                            </div>
                        )}
                        {/* 3. Email — add only; on edit the member email shows as the dialog
                              description (the PUT contract at admin_users.py:304 accepts no email) */}
                        {!isEdit && (
                            <div>
                                <Label>Email *</Label>
                                <Input
                                    type="email"
                                    value={form.email || ''}
                                    onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                                    placeholder="user@company.com"
                                    className="mt-1"
                                    data-testid="add-user-email"
                                />
                            </div>
                        )}
                        {/* 4. Password — add only (create contract requires it) */}
                        {!isEdit && (
                            <div>
                                <Label>Password *</Label>
                                <Input
                                    type="password"
                                    value={form.password || ''}
                                    onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
                                    placeholder="Min 8 characters"
                                    className="mt-1"
                                    data-testid="add-user-password"
                                />
                            </div>
                        )}
                        {/* 5-7. Organization Role, Special Permissions, Department Access — edit only */}
                        {isEdit && (() => {
                            const roleChanged = editUser && (form.org_role !== editUser.org_role || JSON.stringify(form.pillar_roles || {}) !== JSON.stringify(editUser.pillar_roles || {}));
                            const savedWide = Boolean(editUser?.holds_view_all);
                            return <div className="space-y-1" data-testid="contact-visibility-field">
                                <Label>Contact visibility</Label>
                                <select value={form.contact_visibility || 'all'} onChange={e => setForm(f => ({ ...f, contact_visibility: e.target.value }))}
                                    className="w-full mt-1 px-3 py-2 text-sm border border-stone-200 rounded-lg bg-white" data-testid="contact-visibility-select">
                                    <option value="all">All contacts</option>
                                    <option value="assigned">Assigned contacts only</option>
                                </select>
                                {savedWide && <p className="text-[11px] text-stone-500" data-testid="contact-visibility-role-note">
                                    {roleChanged ? 'Has org-wide access through their current role. Whether the new role has it shows after you save.' : 'Has org-wide access through their role. This setting has no effect unless the role changes.'}
                                </p>}
                            </div>;
                        })()}
                        {isEdit && (
                            <PillarRoleEditor
                                orgRole={form.org_role}
                                pillarRoles={form.pillar_roles}
                                isAgentDeveloper={form.is_agent_developer}
                                onChange={onPillarChange}
                            />
                        )}
                        {/* 8. Groups — both modes */}
                        {groups.length > 0 && (
                            <div>
                                <Label>Groups</Label>
                                <div className="flex flex-wrap gap-2 mt-1">
                                    {groups.map((g) => (
                                        <label
                                            key={g.group_id}
                                            className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-xs rounded-lg border cursor-pointer transition-colors ${
                                                form.group_ids?.includes(g.group_id)
                                                    ? 'bg-stone-900 text-white border-stone-900'
                                                    : 'bg-white text-stone-600 border-stone-200 hover:border-stone-400'
                                            }`}
                                        >
                                            <input
                                                type="checkbox"
                                                className="hidden"
                                                checked={form.group_ids?.includes(g.group_id) || false}
                                                onChange={(e) =>
                                                    setForm((f) => ({
                                                        ...f,
                                                        group_ids: e.target.checked
                                                            ? [...(f.group_ids || []), g.group_id]
                                                            : (f.group_ids || []).filter((id) => id !== g.group_id),
                                                    }))
                                                }
                                            />
                                            {g.name}
                                        </label>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                    <DialogFooter className="shrink-0 border-t border-stone-100 bg-white px-5 py-3">
                        <button
                            type="button"
                            className="px-4 py-2 text-sm text-stone-600 hover:text-stone-900 transition-colors"
                            onClick={() => onOpenChange(false)}
                            disabled={saving}
                        >
                            Cancel
                        </button>
                        <button
                            type={isEdit ? 'button' : 'submit'}
                            className="px-4 py-2 text-sm bg-stone-900 text-white rounded-lg hover:bg-stone-800 transition-colors disabled:opacity-50"
                            onClick={isEdit ? onSubmit : undefined}
                            disabled={saving || (isEdit && roleConflict)}
                            title={isEdit && roleConflict ? 'Resolve the work-role conflict inside the dialog first' : undefined}
                            data-testid={isEdit ? 'edit-user-submit' : 'add-user-submit'}
                        >
                            {saving ? 'Saving…' : isEdit ? 'Save Changes' : 'Add Member'}
                        </button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}
